import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, BehaviorSubject, throwError } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface VehicleBrand {
  id: number;
  name: string;
}

export interface VehicleModel {
  id: number;
  name: string;
  brand_id: number;
}

export interface MotorcycleBrand {
  id: number;
  name: string;
}

interface BrandsAndModelsResponse {
  status: string;
  message: string;
  data: {
    brands: VehicleBrand[];
    models: VehicleModel[];
  };
}

interface BrandsResponse {
  status: string;
  message: string;
  data: VehicleBrand[];
}

interface MotorcycleBrandsResponse {
  status: string;
  message: string;
  data: MotorcycleBrand[];
}

interface ModelsResponse {
  status: string;
  message: string;
  data: VehicleModel[];
}

@Injectable({
  providedIn: 'root',
})
export class VehicleDataService {
  private readonly AUTOMOBILE_API_BASE = `${environment.apiUrl}/vehicle-data/automobile`;
  private readonly MOTORCYCLE_API_BASE = `${environment.apiUrl}/vehicle-data/motorcycle`;

  private brandsCache$ = new BehaviorSubject<VehicleBrand[]>([]);
  private allModelsCache$ = new BehaviorSubject<VehicleModel[]>([]);
  private hasLoadedAutomobileData = false;
  private motorcycleBrandsCache$ = new BehaviorSubject<MotorcycleBrand[]>([]);
  private hasLoadedMotorcycleData = false;

  constructor(private http: HttpClient) {
    this.loadBrandsAndModels();
  }

  getBrands(): Observable<VehicleBrand[]> {
    if (this.brandsCache$.value.length === 0 && !this.hasLoadedAutomobileData) {
      this.loadBrandsAndModels();
    }
    return this.brandsCache$.asObservable();
  }

  getModelsByBrand(brandId: number): Observable<VehicleModel[]> {
    return this.allModelsCache$.pipe(
      map(models => models.filter(model => model.brand_id === brandId))
    );
  }

  searchBrands(query: string): Observable<VehicleBrand[]> {
    return this.getBrands().pipe(
      map((brands) =>
        brands.filter((brand) =>
          brand.name.toLowerCase().includes(query.toLowerCase())
        )
      )
    );
  }

  searchModels(brandId: number, query: string): Observable<VehicleModel[]> {
    return this.getModelsByBrand(brandId).pipe(
      map((models) =>
        models.filter((model) =>
          model.name.toLowerCase().includes(query.toLowerCase())
        )
      )
    );
  }

  private loadBrandsAndModels(): void {
    const url = `${this.AUTOMOBILE_API_BASE}/all`;

    this.http.get<BrandsAndModelsResponse>(url).pipe(
      tap(response => {
        if (response.status === 'success' && response.data) {
          this.brandsCache$.next(response.data.brands);
          this.allModelsCache$.next(response.data.models);
          this.hasLoadedAutomobileData = true;
        }
      }),
      catchError(error => {
        console.error('Error loading brands and models:', error);
        this.hasLoadedAutomobileData = false;
        return throwError(() => error);
      })
    ).subscribe();
  }

  reloadAutomobileData(): Observable<boolean> {
    const url = `${this.AUTOMOBILE_API_BASE}/all`;

    return this.http.get<BrandsAndModelsResponse>(url).pipe(
      map(response => {
        if (response.status === 'success' && response.data) {
          this.brandsCache$.next(response.data.brands);
          this.allModelsCache$.next(response.data.models);
          this.hasLoadedAutomobileData = true;
          return true;
        }
        return false;
      }),
      catchError(error => {
        console.error('Error reloading brands and models:', error);
        return of(false);
      })
    );
  }

  getBrandById(brandId: number): VehicleBrand | undefined {
    return this.brandsCache$.value.find(brand => brand.id === brandId);
  }

  getModelById(modelId: number): VehicleModel | undefined {
    return this.allModelsCache$.value.find(model => model.id === modelId);
  }

  clearAutomobileCache(): void {
    this.brandsCache$.next([]);
    this.allModelsCache$.next([]);
    this.hasLoadedAutomobileData = false;
    this.loadBrandsAndModels();
  }

  isAutomobileDataLoaded(): boolean {
    return this.hasLoadedAutomobileData && this.brandsCache$.value.length > 0;
  }

  getMotorcycleBrands(): Observable<MotorcycleBrand[]> {
    if (this.motorcycleBrandsCache$.value.length === 0 && !this.hasLoadedMotorcycleData) {
      this.loadMotorcycleBrands();
    }
    return this.motorcycleBrandsCache$.asObservable();
  }

  private loadMotorcycleBrands(): void {
    const url = `${this.MOTORCYCLE_API_BASE}/brands`;

    this.http.get<MotorcycleBrandsResponse>(url).pipe(
      tap(response => {
        if (response.status === 'success' && response.data) {
          this.motorcycleBrandsCache$.next(response.data);
          this.hasLoadedMotorcycleData = true;
        }
      }),
      catchError(error => {
        console.error('Error loading motorcycle brands:', error);
        this.hasLoadedMotorcycleData = false;
        return throwError(() => error);
      })
    ).subscribe();
  }

  reloadMotorcycleBrands(): Observable<boolean> {
    const url = `${this.MOTORCYCLE_API_BASE}/brands`;

    return this.http.get<MotorcycleBrandsResponse>(url).pipe(
      map(response => {
        if (response.status === 'success' && response.data) {
          this.motorcycleBrandsCache$.next(response.data);
          this.hasLoadedMotorcycleData = true;
          return true;
        }
        return false;
      }),
      catchError(error => {
        console.error('Error reloading motorcycle brands:', error);
        return of(false);
      })
    );
  }

  getMotorcycleBrandById(brandId: number): MotorcycleBrand | undefined {
    return this.motorcycleBrandsCache$.value.find(brand => brand.id === brandId);
  }

  searchMotorcycleBrands(query: string): Observable<MotorcycleBrand[]> {
    return this.getMotorcycleBrands().pipe(
      map((brands) =>
        brands.filter((brand) =>
          brand.name.toLowerCase().includes(query.toLowerCase())
        )
      )
    );
  }

  clearMotorcycleCache(): void {
    this.motorcycleBrandsCache$.next([]);
    this.hasLoadedMotorcycleData = false;
    this.loadMotorcycleBrands();
  }

  isMotorcycleDataLoaded(): boolean {
    return this.hasLoadedMotorcycleData && this.motorcycleBrandsCache$.value.length > 0;
  }

  clearAllCaches(): void {
    this.clearAutomobileCache();
    this.clearMotorcycleCache();
  }
}
